import { Direction } from '@game/protocol';

/**
 * Controles de toque (PRD §6).
 *
 * Um direcional de quatro setas em DOM, sobre o canvas. Quatro botões, e não um
 * analógico: o movimento do jogo é cardeal, e um analógico prometeria diagonais que o
 * servidor não aceita — o jogador sentiria o jogo "engolindo" comandos.
 *
 * Aparece só em dispositivo com toque. Num desktop com tela sensível o teclado continua
 * valendo; os dois conviverem não custa nada porque ambos alimentam o mesmo caminho de
 * input.
 */

/**
 * O par pressionar/soltar é entregue **explícito**, e não como um `onHold(direction|null)`.
 *
 * Quem recebe alimenta o mesmo `DirectionState` do teclado, e esse estado precisa saber
 * *qual* direção foi solta para tirá-la da pilha de teclas seguradas. Um `null` obrigaria
 * quem recebe a manter uma segunda cópia de qual dedo estava onde — exatamente o estado
 * duplicado que faz uma direção ficar presa quando os dois caminhos discordam.
 */
export interface TouchHandlers {
  onPress(direction: Direction): void;
  onRelease(direction: Direction): void;
  /**
   * O que o menu abre. A ordem aqui é a ordem na tela.
   *
   * Uma lista, e não um campo por painel (`onToggleInventory`, `onToggleMinimap`, …): quem
   * decide **quais** menus existem é a cena, que é quem os construiu, e cada painel novo
   * custaria aqui um campo, um botão escrito à mão e uma linha de CSS. Com a lista, o preço
   * de dar um menu novo ao celular é uma entrada no array lá — e foi esse preço, pago uma vez
   * por painel, que deixou minimapa, bestiário, amigos e automações presos ao teclado.
   */
  readonly actions: readonly TouchAction[];
}

/** Um item do menu: o rótulo que cabe no botão e o que ele faz. */
export interface TouchAction {
  readonly label: string;
  readonly onPick: () => void;
}

export function isTouchDevice(): boolean {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    window.matchMedia('(pointer: coarse)').matches
  );
}

export class TouchControls {
  private held: Direction | null = null;

  constructor(root: HTMLElement, handlers: TouchHandlers) {
    root.classList.add('touch-controls');
    root.innerHTML = '';

    if (!isTouchDevice()) {
      root.style.display = 'none';
      return;
    }

    // Marca o documento inteiro, porque há CSS fora daqui que depende destes controles
    // existirem — o volume, que morava sozinho no canto de baixo à direita e passa a dividi-lo
    // com o botão do menu (ver `.audio-controls` em `style.css`).
    //
    // Uma classe, e não `@media (pointer: coarse)`: a media query é uma **aproximação** de
    // `isTouchDevice`, e as duas discordam justamente no notebook com tela sensível, onde o
    // ponteiro é fino, o direcional aparece assim mesmo e a sobreposição continuaria. Quem
    // sabe se o direcional está na tela é quem acabou de desenhá-lo.
    document.body.classList.add('com-toque');

    const pad = document.createElement('div');
    pad.className = 'dpad';

    const buttons: Array<[Direction, string, string]> = [
      [Direction.North, '▲', 'up'],
      [Direction.West, '◀', 'left'],
      [Direction.East, '▶', 'right'],
      [Direction.South, '▼', 'down'],
    ];

    for (const [direction, glyph, area] of buttons) {
      const button = document.createElement('button');
      button.className = `dpad-button ${area}`;
      button.type = 'button';
      button.textContent = glyph;

      // `pointerdown`/`pointerup` cobrem toque, caneta e mouse com um só par de eventos.
      button.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        button.setPointerCapture(event.pointerId);
        this.held = direction;
        handlers.onPress(direction);
      });

      const release = (event: PointerEvent): void => {
        event.preventDefault();
        if (this.held !== direction) return;
        this.held = null;
        handlers.onRelease(direction);
      };

      button.addEventListener('pointerup', release);
      button.addEventListener('pointercancel', release);
      // Sem `pointerleave`, arrastar o dedo para fora do botão deixaria a direção presa.
      button.addEventListener('pointerleave', release);

      pad.append(button);
    }

    root.append(pad, this.buildMenu(handlers.actions));
  }

  /**
   * O menu dos painéis — e por que ele nasce fechado atrás de um botão só.
   *
   * No computador, minimapa, bestiário, amigos e automações abrem por tecla, e tecla não
   * ocupa tela. No celular não há tecla nenhuma: sem um gesto próprio, esses quatro painéis
   * simplesmente não existem para quem joga no toque — foi o defeito relatado, e o único
   * botão que havia (`Mochila`) mostra que o buraco era conhecido pela metade.
   *
   * Um botão permanente por painel seria a saída óbvia e a errada: numa tela de celular o
   * cenário já divide espaço com o direcional, a barra de magias, o chat e o painel de status
   * flutuante, e este jogo é justamente aquele em que a criatura entra pela borda. Um botão em
   * repouso e a lista só enquanto se escolhe é o menor pedaço de mundo que dá para cobrir e
   * ainda alcançar tudo.
   *
   * A lista fecha sozinha na escolha. Menu que fica aberto depois de servir vira mais uma
   * coisa para fechar — e aqui ele fecharia justamente por cima do chão em que se anda.
   */
  private buildMenu(actions: readonly TouchAction[]): HTMLElement {
    const menu = document.createElement('div');
    menu.className = 'touch-menu';

    const list = document.createElement('div');
    list.className = 'touch-menu-list';

    const opener = document.createElement('button');
    opener.className = 'touch-action';
    opener.type = 'button';
    opener.textContent = '☰ Menu';
    opener.setAttribute('aria-expanded', 'false');

    const setOpen = (open: boolean): void => {
      menu.classList.toggle('open', open);
      opener.setAttribute('aria-expanded', String(open));
    };

    opener.addEventListener('click', () => setOpen(!menu.classList.contains('open')));

    for (const action of actions) {
      const item = document.createElement('button');
      item.className = 'touch-menu-item';
      item.type = 'button';
      item.textContent = action.label;
      item.addEventListener('click', () => {
        setOpen(false);
        action.onPick();
      });

      list.append(item);
    }

    // O botão primeiro, a lista depois — ordem de leitura, e não de desenho: onde a lista
    // aparece quem decide é o CSS, que a ancora acima da faixa do direcional (ver
    // `.touch-menu-list`). Aqui o que a ordem define é o caminho de quem navega em sequência,
    // e menu antes do botão que o abre é a ordem invertida.
    menu.append(opener, list);
    return menu;
  }
}
